import { Component, effect, signal } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SignService } from '@libs/app.services/sign/sign.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IHttpResponse, API_ERRORS, DRAW_SCHEDULE, CURRENCY } from '@libs/interfaces';
import { tap, map, Observable, debounceTime } from 'rxjs';
import { Router } from '@angular/router';
import { EmailInputComponent } from '../email-input/email-input';
import { PhoneInputComponent } from '../phone-input/phone-input';
import { UserService } from '@libs/app.services/account/user.service';
import { LangService } from '@libs/app.services/lang.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormValidatorsService } from '@libs/app.services/form-validators.service';
import { SubmitSpinnerComponent } from '@libs/stand-alone-components/commons/submit-spinner/submit-spinner';
import { LocalstorageService } from '@libs/app.services/landing/localstorage.service';
@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',

  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    EmailInputComponent,
    PhoneInputComponent,
    SubmitSpinnerComponent,
  ],
})
export class SignUpComponent {
  loading = signal(false);

  dataForm = signal<FormGroup>(
    this.formBuilder.group({
      email: [
        '',
        {
          validators: Validators.compose([Validators.required, Validators.email]),
          asyncValidators: [this.formValidatorsService.emailValidator()],
        },
      ],
      phone: [
        '',
        {
          validators: Validators.required,
          asyncValidators: [this.phoneValidator('55')],
        },
      ],
      phonePrefix: [55, [Validators.required]],
      country: ['BR'],
      draw_schedule: [DRAW_SCHEDULE.RIO],
      currency: [CURRENCY.BRL],
      lang: [this.lang.lang],
      referral: [this.ls.referral?.referer],
    }),
  );
  showWarning$: Observable<string>;
  value = toSignal(this.dataForm().valueChanges.pipe(debounceTime(500)));
  phoneExistError = signal(false);
  emailExistError = signal(false);
  valid = signal(false);
  constructor(
    private formBuilder: FormBuilder,
    private sign: SignService,
    private router: Router,
    private userService: UserService,
    private lang: LangService,
    private ls: LocalstorageService,
    private formValidatorsService: FormValidatorsService,
  ) {
    effect(() => {
      if (this.value()) {
        this.phoneExistError.set(this.dataForm().get('phone').errors?.['phoneExists']);
        this.emailExistError.set(this.dataForm().get('email').errors?.['emailExists']);
        this.valid.set(this.dataForm().valid);
      }
    });
  }
  private phoneValidator(phonePrefix: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> =>
      this.userService
        .checkIfPhoneExists(control.value, phonePrefix)
        .pipe(map(res => (res ? { phoneExists: true } : null)));
  }

  submit() {
    if (this.valid() && !this.loading()) {
      this.loading.set(true);
      this.showWarning$ = null;
      const value = this.dataForm().value;
      this.showWarning$ = this.sign.createTmpUser(value).pipe(
        tap(() => this.loading.set(false)),
        map((response: IHttpResponse) => this.checkResponse(value.email, response)),
      );
    }
  }
  private checkResponse(value: string, response: IHttpResponse) {
    switch (response.error) {
      case API_ERRORS.ERROR_RESPONSE:
        return 'ERRORS.APIE';
      default:
        this.router.navigate(['/sign/code'], { queryParams: { value: value } });
        return '';
    }
  }
}
